import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  adoptCommunityVersion,
  approveApproval,
  commentApproval,
  getApproval,
  getApprovalContext,
  getApprovalForObject,
  getApprovalSummary,
  listApprovals,
  listMyApprovals,
  rejectApproval,
  submitApproval,
} from '../api';
import type { ApprovalContext, ApprovalRequest, ObjectType } from '../types';

export const APPROVALS_KEY = 'approvals';
export const APPROVAL_CONTEXT_KEY = 'approval-context';
export const APPROVAL_SUMMARY_KEY = 'approval-summary';
export const MY_APPROVALS_KEY = 'my-approvals';

const useApprovalScope = () =>
  useSelector((state: any) => `${state.auth.current_company}:${state.auth.current_employee}`);

/**
 * GET /approvals/context/ — gating menu a akcí workflow pro kohokoliv (nahrazuje
 * čtení admin-only company detailu). refetchOnWindowFocus zůstává zapnutý: nově
 * přiřazený schvalovatel uvidí menu po návratu do okna bez reloadu.
 */
export const useApprovalContext = () => {
  const scope = useApprovalScope();
  const companyId = useSelector((state: any) => state.auth.current_company);
  return useQuery<ApprovalContext>({
    queryKey: [APPROVAL_CONTEXT_KEY, scope],
    queryFn: getApprovalContext,
    enabled: !!companyId,
  });
};

export const canSeeApprovalQueue = (ctx?: ApprovalContext): boolean =>
  !!ctx && ctx.enabled && (ctx.is_admin || ctx.is_approver);

/** Stránkovaný seznam žádostí s filtry (stav + typ entity). */
export const useApprovalsList = (params: {
  status?: string;
  objectType?: string;
  page?: number;
}) => {
  const scope = useApprovalScope();
  return useQuery({
    queryKey: [APPROVALS_KEY, 'list', scope, params],
    queryFn: () => listApprovals(params),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

/** Nekonečné načítání žádostí (infinite scroll) s filtry (stav + typ entity). */
export const useApprovalsInfinite = (params: { status?: string; objectType?: string }) => {
  const scope = useApprovalScope();
  return useInfiniteQuery({
    queryKey: [APPROVALS_KEY, 'infinite', scope, params],
    queryFn: ({ pageParam }) => listApprovals({ ...params, page: pageParam, page_size: 30 }),
    // Backend (CorePageNumberPagination) vrací `next` jako číslo stránky, na poslední null.
    getNextPageParam: (lastPage) => lastPage?.next ?? undefined,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

/** Detail žádosti včetně `events` timeline. */
export const useApprovalDetail = (id?: string) => {
  const scope = useApprovalScope();
  return useQuery({
    queryKey: [APPROVALS_KEY, 'detail', id, scope],
    queryFn: () => getApproval(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/** Moje žádosti (jako autor / schvalovatel). */
export const useMyApprovals = (enabled = true) => {
  const scope = useApprovalScope();
  return useQuery({
    queryKey: [MY_APPROVALS_KEY, scope],
    queryFn: listMyApprovals,
    enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Active (draft/pending/rejected) request for a given item via /approvals/for-object/ —
 * covers any author (an admin sees drafts of others too). Submit permission is enforced
 * by the backend (author or admin).
 */
export const useMyApprovalForObject = (
  objectType: ObjectType,
  objectId?: string,
  enabled = true
) => {
  const scope = useApprovalScope();
  const { data, isLoading } = useQuery({
    queryKey: [MY_APPROVALS_KEY, 'for-object', objectType, objectId, scope],
    queryFn: () => getApprovalForObject(objectType, objectId as string),
    enabled: enabled && !!objectId,
    refetchOnWindowFocus: false,
  });
  return { request: (data ?? undefined) as ApprovalRequest | undefined, isLoading };
};

/** Souhrnné počty (jen pro admina — volá si volající, když má smysl). */
export const approvalSummaryKey = (companyId: unknown) => [APPROVAL_SUMMARY_KEY, companyId];

export const useApprovalSummary = (enabled = true) => {
  const companyId = useSelector((state: any) => state.auth.current_company);
  return useQuery({
    queryKey: approvalSummaryKey(companyId),
    queryFn: getApprovalSummary,
    enabled: enabled && !!companyId,
    refetchOnWindowFocus: false,
  });
};

/** Po každé akci zneplatní seznamy + souhrn + případný detail. */
const useApprovalInvalidate = () => {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries([APPROVALS_KEY]);
    queryClient.invalidateQueries([MY_APPROVALS_KEY]);
    queryClient.invalidateQueries([APPROVAL_SUMMARY_KEY]);
    if (id) queryClient.invalidateQueries([APPROVALS_KEY, 'detail', id]);
  };
};

export const useSubmitApproval = () => {
  const invalidate = useApprovalInvalidate();
  return useMutation({
    mutationFn: (id: string) => submitApproval(id),
    onSuccess: (_res, id) => {
      toast.success('Žádost odeslána ke schválení');
      invalidate(id);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useApproveApproval = () => {
  const invalidate = useApprovalInvalidate();
  return useMutation({
    mutationFn: (id: string) => approveApproval(id),
    onSuccess: (_res, id) => {
      toast.success('Žádost schválena');
      invalidate(id);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useRejectApproval = () => {
  const invalidate = useApprovalInvalidate();
  return useMutation({
    mutationFn: (vars: { id: string; comment: string }) => rejectApproval(vars.id, vars.comment),
    onSuccess: (_res, vars) => {
      toast.success('Žádost zamítnuta');
      invalidate(vars.id);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useCommentApproval = () => {
  const invalidate = useApprovalInvalidate();
  return useMutation({
    mutationFn: (vars: { id: string; comment: string }) => commentApproval(vars.id, vars.comment),
    onSuccess: (_res, vars) => {
      toast.success('Komentář přidán');
      invalidate(vars.id);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useAdoptCommunityVersion = () => {
  const invalidate = useApprovalInvalidate();
  return useMutation({
    mutationFn: adoptCommunityVersion,
    onSuccess: () => {
      toast.success('Komunitní verze převzata jako firemní koncept');
      invalidate();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};
